import { useMemo, useState } from 'react';
import pluralize from 'pluralize';

import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { ComponentMultipleChoice } from 'approot/activity-builder/components/component.constants';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import {
  ClassAnswerNoStudents,
  ClassAnswerNoAnswers,
} from '../class-answer-empty';
import { useGetClassStudents } from '../classroom-student.hooks';
import { prepareResultsPerStudent } from './multiple-choice-individual-response.utils';
import { Box } from 'approot/shared/design-system/box/box.style';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from '../classroom-answer.style';
import { useFindComponentDimensions } from 'approot/activity-builder/components/component.hooks';
import { COMPONENT_SELECTOR } from 'approot/activity-builder/components/shared/logic/get-component-selector-id';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import MultipleChoiceSelectedAnswers from 'approot/activity-builder/components/multiple-choice/multiple-choice-selected-answers';
import MultipleChoiceAttempts from './multiple-choice-attempts';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { useGetStudentAnswersSummaryPollingFallback } from '../use-get-student-answers-summary';

type Props = {
  componentId: string;
  component: Omit<ComponentMultipleChoice, '__typename'>;
};

const MultipleChoiceIndividualResponses = ({
  componentId,
  component,
}: Props) => {
  const [studentDetailsId, setStudentDetailsId] = useState<number | null>();

  const { width, height } = useFindComponentDimensions(
    component.id,
    COMPONENT_SELECTOR
  );

  const { userClassId, contentId, contentType, version } =
    useActivityBuilderPresentContext();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const { data: summaryData, error: summaryError } =
    useGetStudentAnswersSummaryPollingFallback({
      userClassId,
      componentId,
      contentId,
      contentType,
      contentVersion: version!,
    });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const lastStudentResponses = useMemo(
    () => summaryData?.getClassStudentsAnswersSummaryPerComponent || [],
    [summaryData]
  );

  const results = useMemo(() => {
    return prepareResultsPerStudent({
      studentsInClass,
      lastStudentResponses,
      options: component.properties.multipleChoice.options,
    });
  }, [
    studentsInClass,
    lastStudentResponses,
    component.properties.multipleChoice.options,
  ]);

  const studentDetails = useMemo(
    () => results.find(result => result.studentId === studentDetailsId),
    [results, studentDetailsId]
  );

  return (
    <ClassroomAnswerInstruction>
      {studentDetails ? (
        <ActivitySheetInnerMax>
          <MultipleChoiceAttempts
            component={component}
            student={studentDetails}
            studentsInClass={studentsInClass}
            onSelect={setStudentDetailsId}
          />
        </ActivitySheetInnerMax>
      ) : studentsLoading || !summaryData ? (
        <Bouncer />
      ) : summaryError || studentsError ? (
        <ErrorMessageActionable
          ariaPage="Individual Responses"
          paddingTop={0}
        />
      ) : studentsInClass.length === 0 ? (
        <ClassAnswerNoStudents />
      ) : lastStudentResponses.length === 0 ? (
        <ClassAnswerNoAnswers />
      ) : (
        <SubActivityView subActivityDetails={component.subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {result.answer ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => setStudentDetailsId(result.studentId)}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <ActivityBuilderComponentPreview
                        content={{
                          width,
                          height,
                        }}
                      >
                        <MultipleChoiceSelectedAnswers
                          {...component}
                          isResponsive={false}
                          showAnswer
                          selectedAnswers={result.answer}
                        />
                      </ActivityBuilderComponentPreview>
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                  secondaryText={
                    result.attempts > 0
                      ? `${result.attempts} ${pluralize(
                          'attempt',
                          result.attempts
                        )}`
                      : undefined
                  }
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default MultipleChoiceIndividualResponses;
